import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState([]);
  const [impersonating, setImpersonating] = useState(false);

  const carregarPermissoes = async () => {
    try {
      const response = await api.get('/api/usuarios/me/permissions');
      setPermissions(response.data.permissions || []);
    } catch (error) {
      console.error('Erro ao carregar permissões:', error);
      setPermissions([]);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    const hasOriginalToken = sessionStorage.getItem('impersonation_original_token');
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setImpersonating(!!hasOriginalToken);
      Promise.all([
        api.get('/api/auth/me'),
        api.get('/api/usuarios/me/permissions').catch(() => ({ data: { permissions: [] } }))
      ])
        .then(([userRes, permRes]) => {
          setUser(userRes.data.user);
          setPermissions(permRes.data.permissions || []);
        })
        .catch(() => {
          localStorage.removeItem('token');
          sessionStorage.removeItem('impersonation_original_token');
          setImpersonating(false);
        })
        .finally(() => setLoading(false));
    } else {
      setImpersonating(false);
      setLoading(false);
    }
  }, []);

  const login = async (email, senha) => {
    try {
      const response = await api.post('/api/auth/login', { email, senha });
      const { token, user } = response.data;
      localStorage.setItem('token', token);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(user);
      await carregarPermissoes();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Erro ao fazer login' };
    }
  };

  /** Faz login com token e user já obtidos (ex.: após redefinir senha). */
  const loginWithSession = async (token, userData) => {
    if (!token || !userData) return;
    localStorage.setItem('token', token);
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
    await carregarPermissoes();
  };

  const logout = () => {
    sessionStorage.removeItem('impersonation_original_token');
    localStorage.removeItem('token');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
    setPermissions([]);
    setImpersonating(false);
  };

  const startImpersonation = async (targetUserId) => {
    try {
      const response = await api.post('/api/auth/impersonate', { target_user_id: targetUserId });
      const { token, originalToken, user } = response.data;
      sessionStorage.setItem('impersonation_original_token', originalToken);
      localStorage.setItem('token', token);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(user);
      setImpersonating(true);
      await carregarPermissoes();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Erro ao acessar como usuário' };
    }
  };

  const stopImpersonation = () => {
    const originalToken = sessionStorage.getItem('impersonation_original_token');
    if (!originalToken) return;
    sessionStorage.removeItem('impersonation_original_token');
    localStorage.setItem('token', originalToken);
    api.defaults.headers.common['Authorization'] = `Bearer ${originalToken}`;
    setImpersonating(false);
    api.get('/api/auth/me')
      .then((res) => {
        setUser(res.data.user);
        return carregarPermissoes();
      })
      .catch(() => logout());
  };

  const hasPermission = (recurso, acao) => {
    if (user?.role === 'admin_sistema' || user?.email === 'admin@greencheck.com') return true;
    return permissions.some(
      perm => perm.recurso === recurso && perm.acao === acao
    );
  };

  return (
    <AuthContext.Provider value={{ user, login, loginWithSession, logout, loading, permissions, hasPermission, refreshPermissions: carregarPermissoes, impersonating, startImpersonation, stopImpersonation }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
